import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeUp, ParallaxImage } from "@/components/motion";
import FinishSwatch from "@/components/FinishSwatch";
import CategoryIcon from "@/components/CategoryIcon";
import FinishMarquee from "@/components/FinishMarquee";
import { PRODUCTS, HERO_POSTER, LIFESTYLE_IMG } from "@/data/products";
import { useSite, assetUrl, DEFAULT_SITE } from "@/content";

const HERO_IMG =
  "https://images.unsplash.com/photo-1604709177225-055f99402ea3?q=80&w=1920&auto=format&fit=crop";

const TILE_LAYOUT = [
  { label: "Tapware", to: "/tapware", span: "md:col-span-7", h: "h-[340px] md:h-[520px]" },
  { label: "Bathtubs", to: "/bathtubs", span: "md:col-span-5", h: "h-[340px] md:h-[520px]" },
  { label: "Basins", to: "/basins", span: "md:col-span-5", h: "h-[340px] md:h-[440px]" },
  { label: "LED Mirrors", to: "/led-mirrors", span: "md:col-span-7", h: "h-[340px] md:h-[440px]" },
  { label: "Vanities", to: "/vanities", span: "md:col-span-12", h: "h-[300px] md:h-[380px]" },
];

const HeroMedia = ({ src, type }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (v) {
      v.muted = true;
      v.play().catch(() => {});
    }
  }, [src, type]);
  if (type !== "video") {
    return (
      <img
        src={assetUrl(src)}
        alt="SanCub — architectural tapware and bathroom fixtures"
        className="absolute inset-0 h-full w-full object-cover"
        data-testid="hero-image"
      />
    );
  }
  return (
    <video
      ref={ref}
      key={src}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      disablePictureInPicture
      poster={HERO_IMG}
      className="absolute inset-0 h-full w-full object-cover"
      data-testid="hero-video"
    >
      <source src={assetUrl(src)} type="video/mp4" />
    </video>
  );
};

const countFor = (label) => PRODUCTS.filter((p) => p.category === label).length;

const TileCarousel = ({ tiles }) => {
  const railRef = useRef(null);
  const [active, setActive] = useState(0);
  const onScroll = () => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 0) return;
    setActive(Math.round((el.scrollLeft / max) * (tiles.length - 1)));
  };
  return (
    <div className="mt-10 md:hidden" data-testid="collections-carousel">
      <div
        ref={railRef}
        onScroll={onScroll}
        className="no-scrollbar -mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-2"
      >
        {tiles.map((t) => (
          <Link
            key={t.label}
            to={t.to}
            data-testid={`carousel-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="group relative block w-[78vw] shrink-0 snap-start overflow-hidden rounded-[2rem] bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          >
            <div className="h-[380px] overflow-hidden">
              <img src={assetUrl(t.image)} alt={t.label} className="h-full w-full object-cover" loading="lazy" />
            </div>
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-3">
              <span className="rounded-full bg-white/85 px-5 py-2.5 font-display text-base font-medium tracking-tight text-ink backdrop-blur-md">
                {t.label}
              </span>
              <span className="rounded-full bg-ink/55 px-3.5 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-white backdrop-blur-md">
                {countFor(t.label)} {countFor(t.label) === 1 ? "product" : "products"}
              </span>
            </div>
          </Link>
        ))}
      </div>
      <div className="mt-5 flex justify-center gap-1.5" data-testid="carousel-dots">
        {tiles.map((t, i) => (
          <span
            key={t.label}
            data-testid={`carousel-dot-${i}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? "w-6 bg-ink" : "w-1.5 bg-ink/15"}`}
          />
        ))}
      </div>
    </div>
  );
};

const FeaturedCarousel = ({ items }) => (
  <div className="mt-10 md:hidden" data-testid="featured-carousel">
    <div className="no-scrollbar -mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-2">
      {items.map((p) => (
        <Link
          key={p.id}
          to={`/product/${p.id}`}
          data-testid={`featured-mcard-${p.id}`}
          className="group w-[64vw] shrink-0 snap-start rounded-[14px] bg-white p-3 pb-4 shadow-[0_14px_34px_rgb(0,0,0,0.09)]"
        >
          <div className="h-[210px] overflow-hidden rounded-lg bg-sand">
            <img
              src={assetUrl(p.image)}
              alt={p.name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              loading="lazy"
            />
          </div>
          <div className="mt-3 px-1">
            <div className="flex items-center justify-between text-[9px] font-bold uppercase tracking-[0.14em] text-ink/40">
              <span>{p.category}</span>
              <span>{p.sku}</span>
            </div>
            <p className="mt-1 line-clamp-2 font-display text-[14px] font-semibold tracking-tight text-ink">{p.name}</p>
          </div>
        </Link>
      ))}
    </div>
  </div>
);

export const ProductCard = ({ p }) => {
  const [finish, setFinish] = useState(p.finishes[0]);
  const img = assetUrl((p.finishImages && p.finishImages[finish]) || p.image);
  return (
    <article
      className="group rounded-[2rem] bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition duration-500 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
      data-testid={`product-card-${p.id}`}
    >
      <Link to={`/product/${p.id}`} data-testid={`product-link-${p.id}`} className="block">
        {p.placeholder ? (
          <div className="flex h-64 items-center justify-center rounded-3xl bg-sand md:h-72">
            <div className="px-6 text-center">
              <CategoryIcon category={p.category} className="mx-auto h-8 w-8 text-ink/25" />
              <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/40" data-testid={`placeholder-note-${p.id}`}>
                Photos coming soon
              </p>
            </div>
          </div>
        ) : (
          <div className="flex h-64 items-center justify-center overflow-hidden rounded-3xl bg-white p-8 md:h-72">
            <motion.img
              key={img}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              src={img}
              alt={`${p.name} — ${finish}`}
              className="max-h-full w-auto max-w-full object-contain transition-transform duration-700 ease-out group-hover:scale-105"
              loading="lazy"
              data-testid={`product-image-${p.id}`}
            />
          </div>
        )}
      </Link>
      <div className="px-3 pb-3 pt-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-ink/50">{p.category}</p>
          {p.sku && <p className="text-[12px] font-medium tracking-wide text-ink/40" data-testid={`product-sku-${p.id}`}>{p.sku}</p>}
        </div>
        <Link to={`/product/${p.id}`} className="block transition-colors duration-300 hover:text-ink/60">
          <h3 className="mt-1.5 font-display text-xl font-medium tracking-tight text-ink">{p.name}</h3>
        </Link>
        {p.finishes.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`product-finishes-${p.id}`}>
            {p.finishes.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFinish(f)}
                aria-pressed={finish === f}
                data-testid={`finish-${p.id}-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium transition duration-300 ${
                  finish === f
                    ? "bg-ink text-white"
                    : "bg-sand text-ink/60 hover:bg-ink/10 hover:text-ink"
                }`}
              >
                <FinishSwatch finish={f} size="h-3 w-3" />
                {f}
              </button>
            ))}
          </div>
        )}
        <Link
          to="/contact"
          data-testid={`product-enquire-${p.id}`}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-ink/10 py-3 text-[13px] font-semibold text-ink transition duration-300 hover:border-ink hover:bg-ink hover:text-white"
        >
          Enquire
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </article>
  );
};

const Home = () => {
  const { site, products } = useSite();
  const tiles = TILE_LAYOUT.map((t) => ({ ...t, image: site.tiles[t.label] || DEFAULT_SITE.tiles[t.label] }));
  const featured = site.featuredIds.map((id) => products.find((p) => p.id === id)).filter(Boolean);

  return (
  <main data-testid="home-page">
    <section className="relative flex min-h-screen items-end overflow-hidden" data-testid="hero-section">
      <HeroMedia src={site.heroMedia} type={site.heroMediaType} />
      <div className="absolute inset-0 bg-black/35" aria-hidden="true" />
      <div className="relative mx-auto w-full max-w-[1500px] px-6 pb-24 pt-40 md:px-10 md:pb-28">
        <FadeUp delay={0.3}>
          <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-white/80">
            {site.heroEyebrow}
          </p>
        </FadeUp>
        <h1 className="mt-6 max-w-4xl font-display text-5xl font-medium leading-[1.05] tracking-tight text-white md:text-7xl lg:text-8xl" data-testid="hero-headline">
          <MaskedLine delay={0.4}>{site.heroLine1}</MaskedLine>
          <MaskedLine delay={0.52}>{site.heroLine2}</MaskedLine>
        </h1>
        <FadeUp delay={0.9} className="mt-8 max-w-md">
          <p className="text-base leading-relaxed text-white/85 md:text-lg">
            {site.heroSub}
          </p>
        </FadeUp>
        <FadeUp delay={1.05} className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/collections"
            data-testid="hero-cta-collections"
            className="group flex items-center gap-2 rounded-full bg-white px-8 py-4 text-[14px] font-semibold text-ink transition duration-300 hover:scale-[1.03] hover:bg-white/90"
          >
            Explore the collection
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/stockists"
            data-testid="hero-cta-stockists"
            className="group flex items-center gap-2 rounded-full border border-white/40 px-8 py-4 text-[14px] font-semibold text-white backdrop-blur-sm transition duration-300 hover:scale-[1.03] hover:bg-white/10"
          >
            Find a stockist
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </FadeUp>
      </div>
    </section>

    <FinishMarquee />

    <section className="mx-auto max-w-[1500px] px-6 py-20 md:px-10 md:py-28" data-testid="film-section">
      <FadeUp>
        <div className="relative overflow-hidden rounded-[3rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <ParallaxImage
            src={HERO_IMG}
            alt="Grey marble ensuite with LED mirrors, black basins and a freestanding bath"
            className="h-[380px] md:h-[600px]"
            speed={0.08}
          />
          <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10">
            <p className="rounded-full bg-white/80 px-5 py-2.5 text-[13px] font-semibold text-ink backdrop-blur-md">
              Basins, mirrors, baths and tapware — one language
            </p>
          </div>
        </div>
      </FadeUp>
    </section>

    <section className="mx-auto max-w-[1500px] px-6 pb-24 md:px-10 md:pb-36" data-testid="range-section">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <h2 className="max-w-xl font-display text-4xl font-medium leading-[1.1] tracking-tight text-ink md:text-5xl" data-testid="range-headline">
          <MaskedLine inView>One considered language</MaskedLine>
          <MaskedLine inView delay={0.12}>for the whole bathroom.</MaskedLine>
        </h2>
        <FadeUp delay={0.2}>
          <Link
            to="/collections"
            data-testid="range-view-all"
            className="group flex w-fit items-center gap-2 text-[14px] font-semibold text-ink transition-colors duration-300 hover:text-ink/60"
          >
            View all collections
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>

      <TileCarousel tiles={tiles} />
      <div className="mt-14 hidden md:grid md:grid-cols-12 md:gap-8" data-testid="collections-bento">
        {tiles.map((t) => (
          <FadeUp key={t.label} className={`col-span-1 ${t.span}`}>
            <Link to={t.to} data-testid={`bento-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`} className="group relative block overflow-hidden rounded-[2rem] bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className={`${t.h} overflow-hidden`}>
                <img
                  src={assetUrl(t.image)}
                  alt={t.label}
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
              <div className="absolute bottom-5 left-5 flex items-center gap-3">
                <span className="rounded-full bg-white/80 px-5 py-2.5 font-display text-base font-medium tracking-tight text-ink backdrop-blur-md">
                  {t.label}
                </span>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/80 text-ink opacity-0 backdrop-blur-md transition duration-300 group-hover:opacity-100">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          </FadeUp>
        ))}
      </div>
    </section>

    <section className="mx-auto max-w-[1500px] px-6 pb-24 md:px-10 md:pb-36" data-testid="featured-section">
      <FadeUp>
        <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-ink md:text-5xl" data-testid="featured-headline">
          Featured pieces
        </h2>
      </FadeUp>
      <FeaturedCarousel items={featured} />
      <div className="mt-12 hidden gap-6 md:grid md:grid-cols-2 lg:grid-cols-4">
        {featured.map((p, i) => (
          <FadeUp key={p.id} delay={i * 0.07}>
            <ProductCard p={p} />
          </FadeUp>
        ))}
      </div>
    </section>

    <section className="mx-auto max-w-[1500px] px-6 pb-24 md:px-10 md:pb-36" data-testid="lifestyle-section">
      <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-12 md:gap-16">
        <div className="md:col-span-7">
          <ParallaxImage
            src={LIFESTYLE_IMG}
            alt="Serenique fluted freestanding bath in a sunlit bathroom overlooking the hills"
            className="h-[420px] rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:h-[640px]"
            speed={0.1}
          />
        </div>
        <div className="md:col-span-5">
          <FadeUp>
            <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/50">Spotlight</p>
          </FadeUp>
          <h3 className="mt-5 font-display text-4xl font-medium leading-[1.1] tracking-tight text-ink md:text-5xl" data-testid="lifestyle-headline">
            <MaskedLine inView>Serenique fluted</MaskedLine>
            <MaskedLine inView delay={0.12}>freestanding bath.</MaskedLine>
          </h3>
          <FadeUp delay={0.25}>
            <p className="mt-7 max-w-md text-base leading-relaxed text-ink/70 md:text-lg">
              A sculptural fluted profile in gloss white, 1500mm of deep, quiet soak. The
              centrepiece around which the rest of the room arranges itself.
            </p>
            <Link
              to="/collections"
              data-testid="lifestyle-cta"
              className="group mt-9 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 text-[14px] font-semibold text-white transition duration-300 hover:scale-[1.03] hover:bg-ink/90"
            >
              View bathtubs
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-[1500px] px-6 pb-28 md:px-10 md:pb-40" data-testid="cta-section">
      <FadeUp>
        <div className="flex flex-col items-start gap-10 rounded-[3rem] bg-white p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:flex-row md:items-center md:justify-between md:p-16">
          <h2 className="max-w-xl font-display text-4xl font-medium leading-[1.1] tracking-tight text-ink md:text-5xl">
            Renovating, building, or just dreaming?
          </h2>
          <Link
            to="/contact"
            data-testid="cta-enquire-btn"
            className="group flex shrink-0 items-center gap-2 rounded-full bg-ink px-8 py-4 text-[14px] font-semibold text-white transition duration-300 hover:scale-[1.03] hover:bg-ink/90"
          >
            Start an enquiry
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </FadeUp>
    </section>
  </main>
  );
};

export default Home;
